import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";

function TableDialog({ open, onOpenChange, value, onSave }) {
  const [t, setT] = useState(value || { label: "", seats: 4, zone: "Inside", is_active: true });
  useEffect(() => setT(value || { label: "", seats: 4, zone: "Inside", is_active: true }), [value]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="table-dialog">
        <DialogHeader><DialogTitle>{value?.id ? "Edit table" : "New table"}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label>Label</Label>
            <Input value={t.label} onChange={(e) => setT({ ...t, label: e.target.value })} data-testid="table-label-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Seats</Label>
              <Input type="number" min={1} value={t.seats} onChange={(e) => setT({ ...t, seats: parseInt(e.target.value || "1") })} data-testid="table-seats-input" />
            </div>
            <div className="space-y-2">
              <Label>Zone</Label>
              <Input value={t.zone} onChange={(e) => setT({ ...t, zone: e.target.value })} placeholder="Inside / Terrace / Bar" data-testid="table-zone-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={t.is_active !== false} onCheckedChange={(v) => setT({ ...t, is_active: Boolean(v) })} />
            Active
          </label>
        </div>
        <DialogFooter>
          <Button onClick={() => onSave(t)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="table-save-button">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTables() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/tables"); setItems(data || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (t) => {
    try {
      if (t.id) {
        await api.patch(`/admin/tables/${t.id}`, { label: t.label, seats: t.seats, zone: t.zone, is_active: t.is_active });
        toast.success("Updated");
      } else {
        await api.post("/admin/tables", { label: t.label, seats: t.seats, zone: t.zone, is_active: t.is_active });
        toast.success("Created");
      }
      setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/tables/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-tables-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">CMS</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Tables</h1>
          </div>
          <Button onClick={() => setEditing({})} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="table-new-button">
            <Plus className="w-4 h-4 mr-1" /> New table
          </Button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="border border-border rounded-xl overflow-x-auto" data-testid="tables-list">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Seats</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((t) => (
                  <TableRow key={t.id} data-testid={`table-row-${t.id}`}>
                    <TableCell>{t.label}</TableCell>
                    <TableCell>{t.seats}</TableCell>
                    <TableCell>{t.zone}</TableCell>
                    <TableCell className={t.is_active ? "text-primary text-xs uppercase tracking-widest" : "text-muted-foreground text-xs uppercase tracking-widest"}>
                      {t.is_active ? "Active" : "Off"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="border-border mr-2" onClick={() => setEditing(t)} data-testid={`table-edit-${t.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="border-border text-destructive" data-testid={`table-delete-${t.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Remove table {t.label}?</AlertDialogTitle></AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(t.id)} className="bg-destructive text-destructive-foreground" data-testid={`table-delete-confirm-${t.id}`}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <TableDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} value={editing} onSave={save} />
      </div>
    </AdminLayout>
  );
}
